import { ConsumptionChartData } from '@vae/server/src/utils/arrays/types/ChartData'
import { ChartDataset } from 'chart.js'
import { t } from 'i18next'

interface DatasetProps {
	charts: ConsumptionChartData
	isDarkMode: boolean
	useQuarterHourlyResolution: boolean
	useBarGraph: boolean
}

interface BaseDatasetOptions {
	type: 'line' | 'bar'
	data: number[]
	label: string
	color: string
	yAxisID?: string
	order?: number
	stack?: string
	useQuarterHourlyResolution?: boolean
}

const createBaseDataset = (
	options: BaseDatasetOptions,
	isDarkMode: boolean
): ChartDataset<'line' | 'bar', number[]> => ({
	type: options.type,
	data: options.data,
	label: options.label,
	backgroundColor: options.color,
	borderColor: options.color,
	borderWidth: options.type === 'bar' ? 0 : 2,
	hoverBackgroundColor: options.color,
	hoverBorderColor: options.color,
	hoverBorderWidth: 2,
	order: options.order ?? 0,
	pointBackgroundColor: options.type === 'line' ? (isDarkMode ? '#0d2d30' : '#ffffff') : undefined,
	pointRadius: options.useQuarterHourlyResolution ? 0 : options.type === 'line' ? 2.5 : undefined,
	yAxisID: options.yAxisID,
	stack: options.stack,
	borderRadius: options.type === 'bar' ? 2 : undefined,
})

export const createConsumptionDataset = ({
	charts,
	isDarkMode,
	useQuarterHourlyResolution,
	useBarGraph,
}: DatasetProps): ChartDataset<'line' | 'bar', number[]> => {
	return createBaseDataset(
		{
			type: useBarGraph ? 'bar' : 'line',
			useQuarterHourlyResolution,
			data: charts.consumption.groupedData.map(item => (item.kwh > 0 ? item.kwh : 0.0001)),
			label: t('pages_electricity:consumptionChart.consumption'),
			color: isDarkMode ? '#3cb49f' : '#52ae32',
			order: 2,
			yAxisID: 'y',
			stack: 'stack',
		},
		isDarkMode
	)
}

export const createExchangePricesDataset = ({
	charts,
	isDarkMode,
	useQuarterHourlyResolution,
}: DatasetProps): ChartDataset<'line' | 'bar', number[]> => {
	return createBaseDataset(
		{
			type: 'line',
			data:
				charts?.exchangePricesWithMarginalGrouped?.groupedData.map(item => item.priceWithAlv) || [],
			label: t('pages_electricity:consumptionChart.transfer'),
			color: '#ffb30d',
			order: -1,
			yAxisID: 'prices',
			useQuarterHourlyResolution,
		},
		isDarkMode
	)
}

export const createTransferPricesWithTaxDataset = ({
	charts,
	isDarkMode,
}: DatasetProps): ChartDataset<'line' | 'bar', number[]> => {
	return createBaseDataset(
		{
			type: 'line',
			data:
				charts?.transferPrices.data.grouped.withoutElectricityPrice.groupedData.map(
					item => item.value
				) || [],
			label: t('pages_electricity:consumptionChart.transfer'),
			color: '#155f65',
			order: 0,
			yAxisID: 'prices',
		},
		isDarkMode
	)
}

export const createTransferPricesWithoutTaxDataset = ({
	charts,
	isDarkMode,
}: DatasetProps): ChartDataset<'line' | 'bar', number[]> => {
	return createBaseDataset(
		{
			type: 'line',
			data:
				charts.transferPrices.data.grouped.costWithoutElectricityPrice.groupedData.map(
					item => item.value
				) || [],
			label: t('pages_electricity:consumptionChart.transfer'),
			color: '#155f65',
			order: 0,
			yAxisID: 'prices',
		},
		isDarkMode
	)
}

export const createTransferPricesWithElectricityDataset = ({
	charts,
	isDarkMode,
}: DatasetProps): ChartDataset<'line' | 'bar', number[]> => {
	return createBaseDataset(
		{
			type: 'line',
			data: charts.consumptionWithExchangePrice.groupedData.map(item => item.value) || [],
			label: t('pages_electricity:consumptionChart.transfer'),
			color: '#155f65',
			order: 0,
			yAxisID: 'prices',
		},
		isDarkMode
	)
}

export const createExchangePriceWithTransferPrice = ({
	charts,
	isDarkMode,
	useQuarterHourlyResolution,
}: DatasetProps): ChartDataset<'line' | 'bar', number[]> => {
	return createBaseDataset(
		{
			type: 'line',
			data:
				charts.transferPrices.data.grouped.withElectricityPrice.groupedData.map(
					item => item.value
				) || [],
			label: t('pages_electricity:consumptionChart.transfer'),
			color: '#155f65',
			order: 0,
			yAxisID: 'prices',
			useQuarterHourlyResolution,
		},
		isDarkMode
	)
}

export const createProductionDataset = ({
	charts,
	isDarkMode,
	useQuarterHourlyResolution,
	useBarGraph,
}: DatasetProps): ChartDataset<'line' | 'bar', number[]> => {
	return createBaseDataset(
		{
			type: useBarGraph ? 'bar' : 'line',
			data: charts.production.data.map(item => item.value),
			label: t('pages_electricity:consumptionChart.production'),
			color: isDarkMode ? '#52ae32' : '#127d27',
			order: 2,
			stack: 'stack',

			useQuarterHourlyResolution,
		},
		isDarkMode
	)
}
